import { useEffect, useRef, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { Send, Bot, User as UserIcon, Sparkles, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";

const MODELS = [
  { label: "Claude Sonnet 4.6", provider: "anthropic", model: "claude-sonnet-4-6" },
  { label: "GPT 5.4", provider: "openai", model: "gpt-5.4" },
  { label: "Gemini 3 Flash", provider: "gemini", model: "gemini-3-flash-preview" },
];

let msgIdCounter = 0;
const newMsgId = () => `m_${Date.now()}_${++msgIdCounter}`;

export default function Tutor() {
  const [subject, setSubject] = useState("Math");
  const [subjects, setSubjects] = useState(["Math", "Science", "English"]);
  const [modelIdx, setModelIdx] = useState(0);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [speakOn, setSpeakOn] = useState(false);
  const [listening, setListening] = useState(false);
  const bottomRef = useRef(null);
  const recogRef = useRef(null);

  const loadSubjects = useCallback(async () => {
    try {
      const r = await api.get("/subjects");
      setSubjects(r.data);
    } catch (err) {
      console.error("tutor subjects load failed:", err);
    }
  }, []);

  useEffect(() => { loadSubjects(); }, [loadSubjects]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const speak = (text) => {
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0; u.pitch = 1.0;
      window.speechSynthesis.speak(u);
    } catch (err) {
      console.warn("speechSynthesis unavailable:", err);
    }
  };
  const stopSpeaking = () => {
    try { window.speechSynthesis.cancel(); } catch (err) { console.warn("stopSpeaking:", err); }
  };
  const toggleSpeak = () => {
    const next = !speakOn;
    setSpeakOn(next);
    if (!next) stopSpeaking();
  };

  const startListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice input needs Chrome or Edge."); return; }
    if (listening) return;
    const r = new SR();
    r.lang = "en-US"; r.interimResults = true; r.continuous = false;
    let finalText = "";
    r.onresult = (ev) => {
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        if (res.isFinal) finalText += res[0].transcript;
        else interim += res[0].transcript;
      }
      setInput((finalText + interim).trim());
    };
    r.onend = () => {
      setListening(false);
      if (finalText.trim()) setTimeout(() => send(finalText.trim()), 100);
    };
    r.onerror = (ev) => {
      console.warn("SpeechRecognition error:", ev?.error);
      setListening(false);
    };
    recogRef.current = r;
    setListening(true);
    r.start();
  };
  const stopListening = () => {
    setListening(false);
    try { recogRef.current?.stop(); } catch (err) { console.warn("stopListening:", err); }
  };

  const send = async (override) => {
    const text = (override ?? input).trim();
    if (!text || streaming) return;
    const userId = newMsgId(); const asstId = newMsgId();
    setMessages((m) => [...m, { id: userId, role: "user", content: text }, { id: asstId, role: "assistant", content: "" }]);
    setInput(""); setStreaming(true);
    if (speakOn) stopSpeaking();
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/tutor/chat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message: text,
          subject,
          session_id: sessionId,
          provider: MODELS[modelIdx].provider,
          model: MODELS[modelIdx].model,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const obj = JSON.parse(line.slice(5).trim());
            if (obj.delta) {
              full += obj.delta;
              setMessages((m) => m.map((x) => x.id === asstId ? { ...x, content: (x.content || "") + obj.delta } : x));
            } else if (obj.session_id) {
              setSessionId(obj.session_id);
            } else if (obj.error) {
              setMessages((m) => m.map((x) => x.id === asstId ? { ...x, content: "Error: " + obj.error } : x));
            }
          } catch (err) {
            console.warn("SSE parse error:", err, line);
          }
        }
      }
      if (speakOn && full) speak(full);
    } catch (e) {
      console.error("tutor chat failed:", e);
      setMessages((m) => m.map((x) => x.id === asstId ? { ...x, content: "Connection error: " + e.message } : x));
    } finally { setStreaming(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm uppercase font-bold text-neutral-500">AI Tutor</div>
          <h1 className="text-4xl font-extrabold">Ask anything, step by step</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="tutor-subject" className="nb-input w-auto" value={subject} onChange={(e) => setSubject(e.target.value)}>
            {subjects.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="tutor-model" className="nb-input w-auto" value={modelIdx} onChange={(e) => setModelIdx(Number(e.target.value))}>
            {MODELS.map((m, i) => <option key={m.model} value={i}>{m.label}</option>)}
          </select>
          <button
            data-testid="tutor-speak-toggle"
            onClick={toggleSpeak}
            className={`nb-btn text-sm px-3 py-2 ${speakOn ? "" : "nb-btn-secondary"}`}
            title={speakOn ? "Voice reply ON" : "Voice reply OFF"}
          >
            {speakOn ? <><Volume2 className="w-4 h-4" /> Voice on</> : <><VolumeX className="w-4 h-4" /> Voice off</>}
          </button>
        </div>
      </div>

      <div className="nb-card mt-6 min-h-[60vh] flex flex-col">
        <div className="flex-1 p-4 sm:p-6 space-y-4 overflow-y-auto" style={{ maxHeight: "60vh" }}>
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Sparkles className="w-10 h-10 mx-auto text-[var(--coral)]" />
              <h3 className="mt-2 text-2xl font-extrabold">Say hi to your tutor</h3>
              <p className="text-neutral-600">Try: "Explain fractions with a pizza example" — or tap the mic and speak.</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="w-9 h-9 flex-none rounded-full bg-[var(--mint)] border-2 border-black flex items-center justify-center"><Bot className="w-5 h-5" /></div>
              )}
              <div
                data-testid={m.role === "assistant" ? `assistant-msg-${i}` : `user-msg-${i}`}
                className={`max-w-[80%] p-4 border-2 border-black rounded-2xl whitespace-pre-wrap ${m.role === "user" ? "bg-[var(--coral)] text-white" : "bg-[var(--bg-highlight)]"}`}
              >
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
              {m.role === "user" && (
                <div className="w-9 h-9 flex-none rounded-full bg-[var(--sun)] border-2 border-black flex items-center justify-center"><UserIcon className="w-5 h-5" /></div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <div className="border-t-2 border-black p-3 flex gap-2">
          <button
            data-testid="tutor-mic"
            onClick={listening ? stopListening : startListening}
            className={`nb-btn px-3 ${listening ? "bg-[var(--sun)] text-black animate-pulse" : "nb-btn-secondary"}`}
            title={listening ? "Stop listening" : "Speak your question"}
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
          <input
            data-testid="tutor-input"
            className="nb-input"
            placeholder={listening ? "Listening…" : "Ask a question…"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
          />
          <button data-testid="tutor-send" onClick={() => send()} disabled={streaming || !input.trim()} className="nb-btn">
            <Send className="w-4 h-4" /> Send
          </button>
        </div>
      </div>
      <p className="text-xs text-neutral-500 mt-2">Voice uses your browser's Web Speech API — Chrome/Edge work best.</p>
    </div>
  );
}
